import { useRecoilValue } from 'recoil';
import { GetSessionError } from '~/backend/connector-api/connector-components';
import { SessionPresentation } from '~/backend/connector-api/connector-schemas';
import { APIType } from '~/backend/types';
import { WorkflowTargetIds } from '~/hooks/workflows/useWorkflowTargetIds';
import { useBackendModels } from '~/hooks/workflows/backend/useBackend';
import useDosePredictionTask from '~/hooks/workflows/connector/tasks/useDosePredictionTask';
import { selectedPatientIdState } from '~/state/atoms/patientAtoms';
import DosePrediction from './DosePrediction';

type DosePredictionConnectorWorkflowProps = {
    sessionData: SessionPresentation | undefined;
    environmentObjectIds: WorkflowTargetIds | undefined;
    isLoadingSession: boolean;
    error: Error | GetSessionError | null;
};

export default function DosePredictionConnectorWorkflow({
    sessionData,
    environmentObjectIds,
    isLoadingSession,
    error,
}: DosePredictionConnectorWorkflowProps) {
    const selectedPatientId = useRecoilValue(selectedPatientIdState);
    const { start: startProcessing, isSubmitting } = useDosePredictionTask({
        imageStudyId:
            environmentObjectIds?.primaryImage?.studyInstanceUID ??
            (sessionData?.payload?.context_image.study_instance_uid || ''),
        imageSeriesId:
            environmentObjectIds?.primaryImage?.seriesInstanceUID ??
            (sessionData?.payload?.context_image.series_instance_uid || ''),
        patientId: sessionData?.payload?.patient.id || selectedPatientId || '',
        structureSetInstanceUid:
            environmentObjectIds?.structureSet?.sopInstanceUID ??
            (sessionData?.payload?.context_structure_set?.sop_instance_uid ||
                ''),
    });
    const { data: availableModels = [], isLoading: isLoadingModels } =
        useBackendModels(APIType.DOSE);

    return (
        <DosePrediction
            onSubmit={startProcessing}
            availableModels={availableModels}
            error={error}
            isLoadingSession={isLoadingSession}
            isSubmitting={isSubmitting}
            isLoadingModels={isLoadingModels}
            showPseudonymization={false}
        />
    );
}
